/**
 * 项目概览统计卡片组件
 */
import React from 'react';
import { Row, Col, Card, Statistic } from 'antd';
import {
  DollarOutlined,
  PercentageOutlined,
  RiseOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { calculateBudgetHealth, formatMoney } from '../../utils/budgetUtils';
import { useAuthStore } from '../../store/authStore';
import { hasSensitivePermission } from '../../utils/sensitivePermissions';

interface OverviewStatsProps {
  project: ProjectOverviewData;
  stats: any;
  isMobile?: boolean;
}

const OverviewStats: React.FC<OverviewStatsProps> = ({
  project,
  stats,
  isMobile = false
}) => {
  const { checkPermission } = useAuthStore();
  const canViewBudget = hasSensitivePermission(checkPermission, 'budget');
  const totalCost = stats?.summary?.totalProjectCost || project.actualCost || 0;
  const budgetHealth = calculateBudgetHealth(project.budget, totalCost);
  const budgetUsageRate = typeof stats?.summary?.budgetUsageRate === 'number' ? stats.summary.budgetUsageRate : budgetHealth.percentage;
  const scheduleStatusLabel = stats?.summary?.scheduleStatus?.label || project.scheduleStatusLabel || '未设置工期';
  const riskTags = stats?.summary?.risk?.riskTags || project.riskTags || [];

  const statCards = [
    {
      title: '累计统计成本',
      value: totalCost,
      prefix: <DollarOutlined />,
      formatter: (val: number) => formatMoney(val, false),
      background: 'rgba(185, 146, 88, 0.1)',
      color: '#b99258'
    },
    {
      title: '预算使用率',
      value: canViewBudget ? budgetUsageRate : '--',
      prefix: <PercentageOutlined />,
      suffix: '%',
      precision: 1,
      background: 'rgba(185, 146, 88, 0.14)',
      color: budgetHealth.color
    },
    {
      title: '手动进度',
      value: stats?.project?.manualProgress ?? project.progress,
      prefix: <RiseOutlined />,
      suffix: '%',
      background: 'rgba(84, 117, 144, 0.1)',
      color: '#547590'
    },
    {
      title: riskTags.length > 0 ? `风险：${riskTags[0]}` : '工期状态',
      value: riskTags.length > 0 ? riskTags.length : scheduleStatusLabel,
      prefix: <CalendarOutlined />,
      suffix: riskTags.length > 0 ? '项' : '',
      background: 'rgba(90, 209, 178, 0.1)',
      color: riskTags.length > 0 ? '#b99258' : '#5ad1b2',
      valueFontSize: riskTags.length > 0 ? undefined : (isMobile ? 14 : 18)
    }
  ];

  return (
    <Row gutter={[isMobile ? 10 : 16, isMobile ? 10 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
      {statCards.map((card, index) => (
        <Col xs={12} sm={12} md={6} key={index}>
          <Card
            size="small"
            style={{
              height: '100%',
              borderRadius: 20,
              background: card.background,
              border: '1px solid rgba(16, 27, 35, 0.08)',
              boxShadow: 'none'
            }}
            styles={{ body: { padding: isMobile ? '12px' : '16px' } }}
          >
            <Statistic
              title={<span style={{ fontSize: isMobile ? 12 : 14, color: '#667986' }}>{card.title}</span>}
              value={card.formatter ? card.formatter(card.value) : card.value}
              prefix={card.prefix}
              suffix={card.suffix}
              precision={card.precision}
              styles={{
                content: {
                  color: card.color,
                  fontSize: card.valueFontSize || (isMobile ? 18 : 24),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  flexWrap: 'nowrap',
                  whiteSpace: 'nowrap'
                }
              }}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default OverviewStats;
